import "./dp-sizing.js";
import { APP_POOL_CONCURRENCY_MAX, APP_POOL_NODE_GROUP_ID_PATTERN, APP_POOL_NODE_TYPE_PATTERN, APP_POOL_WORKLOADS, appPoolNameSchema, computeVectorSchema } from "./app-pool.js";
import { MAX_WORK_TIMEOUT_MS, runStateSchema, runStatusSchema, workAttemptIdSchema, workIdempotencyKeySchema, workRunIdSchema } from "./work.js";
import { APP_POOL_ORG_ID_PATTERN } from "./app-pool-subjects.js";
import { z } from "zod";
//#region src/app-pool-submit.ts
/**
* The wire between the pool client (`@zion/sdk-node` `AppPools`) and the operator's app-pool
* routes (2026-09-04-app-pools, WS-5 §7, WS-6 §3). Every schema is `.strict()`. The request carries
* no identity field: `appKey` and `fixtureId` come from the verified ServiceAccount token and the
* org from the forwarded end-user token or a resident-org claim (chain C, I-14). The response
* shapes `WorkHandle`, `RunStatus`, `WorkEvent` and `CancelOutcome` are the existing ones in
* `work.ts` and are not redeclared; `WORK_REASONS` is reused with no new member. There are no
* attempt routes: the dispatcher claims, renews and finishes in process (R-007).
*/
/** The org id grammar of the app-pool contracts: a lowercase UUID, the canonical form
*  `canonicaliseOrgId` emits and the one form a subject segment carries (R-004). One declaration,
*  in `app-pool-subjects.ts`; the door and the binding import it. */
const appPoolOrgIdSchema = z.string().regex(APP_POOL_ORG_ID_PATTERN, "a lowercase UUID org id");
const APP_POOLS_PATH = "/internal/app-pools";
const APP_POOL_RUNS_PATH = `${APP_POOLS_PATH}/runs`;
const APP_POOL_RUNS_SUBMIT_PATH = `${APP_POOL_RUNS_PATH}/submit`;
const APP_POOL_RUNS_GET_PATH = `${APP_POOL_RUNS_PATH}/get`;
const APP_POOL_RUNS_CANCEL_PATH = `${APP_POOL_RUNS_PATH}/cancel`;
const APP_POOL_RUNS_RESULT_PATH = `${APP_POOL_RUNS_PATH}/result`;
const APP_POOL_RUNS_STREAM_PATH = `${APP_POOL_RUNS_PATH}/stream`;
const APP_POOL_DESCRIBE_PATH = `${APP_POOLS_PATH}/pools/describe`;
const APP_POOL_SIGNALS_PATH = `${APP_POOLS_PATH}/signals`;
/** `nodes`: the observed node census of one pool (#2612). A route of its own beside `describe`.
*  `appPoolStatusSchema` is `.strict()` and parsed by every app's own SDK copy, so a new key on
*  `describe` would break every fixture image built before it. Old clients never call this route.
*  In `APP_POOL_ROUTE_PATHS` since the operator's `nodes` door landed (R-070); the door-completeness
*  arms in the operator and in sdk-node read that list. */
const APP_POOL_NODES_PATH = `${APP_POOLS_PATH}/pools/nodes`;
/** `image`: the digest one pool dispatches and the app version it was read from
*  (2026-09-21-pool-image-per-version-and-door-token-rotation, L-13; #2944, #2810). A route of its
*  own for the reason `nodes` is one: `appPoolStatusSchema` is `.strict()` in every fixture image's
*  SDK copy, so a new key on `describe` would fail every client built before it. */
const APP_POOL_IMAGE_PATH = `${APP_POOLS_PATH}/pools/image`;
/** `runs/describe`: the image one run's attempt runs and the version whose pod submitted it (L-13).
*  Beside `runs/get` for the same reason: `runStatusSchema` is `.strict()` and shared by every work
*  door, so its key set does not move. */
const APP_POOL_RUNS_DESCRIBE_PATH = `${APP_POOL_RUNS_PATH}/describe`;
/** The port a pool pod binds for the operator's pipe when the operator stamps no
*  {@link APP_POOL_POD_PORT_ENV} (the SDK's fallback). The operator renders its own configured value
*  at the container port, both NetworkPolicies and the AuthorizationPolicy, and stamps it; that value
*  defaults to this number (2026-09-11-app-pool-pipe-port R-2). The run context arrives on the pipe as
*  `appPoolRunFrameSchema` (R-007, R-023). The pod's env carries the port and the capability env its
*  `infrastructure` block declares; it carries no run context. 8080 is the isolated pool's
*  `ISOLATED_POOL_PORT`; an operator test pins the two equal. */
const APP_POOL_POD_PORT = 8080;
/** The env variable the operator stamps into every app-pool attempt pod, carrying the port that pod
*  must listen on (R-1 of 2026-09-11-app-pool-pipe-port, which reverses R-020 for this one
*  variable). The operator reads the number from its own config, renders it as the container port,
*  the two NetworkPolicies and the AuthorizationPolicy, dials it, and writes it here, so the pod
*  learns the port instead of being built from the same constant.
*
*  `resolveAppPoolServePort` in `@zion/sdk-node` reads `options.port`, then this variable, then
*  {@link APP_POOL_POD_PORT}. So {@link APP_POOL_POD_PORT} (8080) is the value a pod binds when this
*  variable is absent, which is what an image built before the operator stamped it sees.
*
*  The default stays 8080 in this change. The flip to 61080 is one chart-values line, taken after
*  every pool image carries an SDK that reads this variable: `plan.md` §"The flip" of
*  2026-09-11-app-pool-pipe-port (R-2). Nothing here changes on that day. */
const APP_POOL_POD_PORT_ENV = "ZION_APP_POOL_PORT";
/** Every route the operator serves for app pools, for the door-completeness test. */
const APP_POOL_ROUTE_PATHS = [
	APP_POOL_RUNS_SUBMIT_PATH,
	APP_POOL_RUNS_GET_PATH,
	APP_POOL_RUNS_CANCEL_PATH,
	APP_POOL_RUNS_RESULT_PATH,
	APP_POOL_RUNS_STREAM_PATH,
	APP_POOL_DESCRIBE_PATH,
	APP_POOL_SIGNALS_PATH,
	APP_POOL_NODES_PATH,
	APP_POOL_IMAGE_PATH,
	APP_POOL_RUNS_DESCRIBE_PATH
];
/** Caller-side options (L-10, I-14). `compute` may only narrow the pool's `compute.max`; a widen is
*  `invalid-args` at admission; the platform does not clamp it. `orgId` is the lowercase org UUID
*  (`appPoolOrgIdSchema`), a claim by app code checked for residency on the DP; a verified end-user
*  org wins over it. */
const appPoolSubmitOptionsSchema = z.object({
	idempotencyKey: workIdempotencyKeySchema.optional(),
	timeoutMs: z.number().int().positive().max(MAX_WORK_TIMEOUT_MS).optional(),
	compute: computeVectorSchema.optional(),
	orgId: appPoolOrgIdSchema.optional()
}).strict();
/** The submit body. No `appId`, no `fixtureId`: identity is the token's (chain C hop 1). */
const appPoolSubmitRequestSchema = z.object({
	pool: appPoolNameSchema,
	payload: z.unknown().optional(),
	...appPoolSubmitOptionsSchema.shape
}).strict();
/**
* `get`, `cancel`, `result`, `stream`: the run reference, and the placement opt-in (R-6).
*
* `placement: true` asks the door to carry `nodeId` and `placedAt` on the `RunStatus` it answers
* and on the `{ state }` frames of a stream. Absent asks for today's bytes. `z.literal(true)` and
* not a boolean: `placement: false` would be a second spelling of the absent key, and this schema
* is `.strict()`, so the two spellings would have to answer identically forever.
*
* Opt-in and not a default, in both directions. Every response schema here is `.strict()` and each
* app parses with its own compiled copy, so a key an app did not ask for answers
* `malformed-response` in every image built before the key existed. In the other direction this
* request schema is `.strict()` too, so a client that always sent the key would be refused by an
* operator that predates it, on a data plane that has not rolled.
*
* `cancel` and `result` share this schema and read no placement: their answers carry an outcome and
* a value, not a status. The key is legal there and changes nothing.
*
* The door that reads the key ships in the first DP release carrying this change (planned as
* 1.32.180; the release that lands it is the fact). An operator that predates it parses the body
* with its own `.strict()` copy of this schema and answers `404 unknown-run`, the refusal a
* malformed id takes, so a client sends the key only where it reads the fields.
*/
const appPoolRunRefSchema = z.object({
	runId: workRunIdSchema,
	placement: z.literal(true).optional()
}).strict();
/** `describe`: one pool of the caller's app. */
const appPoolDescribeRequestSchema = z.object({ pool: appPoolNameSchema }).strict();
/** Where the pool stands on placement (I-20, L-11). `provider-local` is the dev arm: no node group,
*  no selector. `unschedulable` carries a reason. */
const APP_POOL_PLACEMENT_STATES = [
	"ready",
	"resolving",
	"unschedulable",
	"provider-local"
];
/** What `describe` returns and the harness's signals derive from. `concurrency`, `warmFloor` and
*  `maxNodes` are the numbers in force after any function knob and clamp. `depth` counts `queued`
*  plus `enqueued`; `inFlight` counts `running`. */
const appPoolStatusSchema = z.object({
	pool: appPoolNameSchema,
	workload: z.enum(APP_POOL_WORKLOADS),
	depth: z.number().int().min(0),
	inFlight: z.number().int().min(0),
	concurrency: z.number().int().min(0),
	warmFloor: z.number().int().min(0),
	maxNodes: z.number().int().min(0),
	groupId: z.string().regex(APP_POOL_NODE_GROUP_ID_PATTERN).nullable(),
	nodeType: z.string().regex(APP_POOL_NODE_TYPE_PATTERN).max(40).nullable(),
	placement: z.enum(APP_POOL_PLACEMENT_STATES),
	unschedulableReason: z.string().min(1).max(512).nullable()
}).strict();
/** `nodes`: one pool of the caller's app, as `describe`. */
const appPoolNodesRequestSchema = z.object({ pool: appPoolNameSchema }).strict();
/** An observed node of a pool's group. `joining`: the Node exists and is not `Ready`; `ready`: the
*  `Ready` condition is `True`; `draining`: `spec.unschedulable` or the cluster autoscaler's
*  deletion taint. Nodes the group has asked for and that have not registered yet are a COUNT
*  (`provisioning`), never entries: an id for a node that does not exist would be invented. */
const APP_POOL_NODE_STATES = [
	"joining",
	"ready",
	"draining"
];
/** The opaque per-node id: `apn-` plus the first 12 hex of sha256 over the Kubernetes node name.
*  Stable across polls for the life of the node. The node name itself embeds the data plane's own
*  name and never crosses this door. The door exposes the hashed `groupId` and the machine type
*  today, and this keeps to that. */
const APP_POOL_NODE_ID_PATTERN = /^apn-[0-9a-f]{12}$/;
const appPoolNodeIdSchema = z.string().regex(APP_POOL_NODE_ID_PATTERN, "an apn- node id");
const appPoolNodeSchema = z.object({
	nodeId: appPoolNodeIdSchema,
	state: z.enum(APP_POOL_NODE_STATES),
	/** The instant the node entered `state`: the `Ready` condition's `lastTransitionTime` for
	*  `ready`, `creationTimestamp` for `joining`, the deletion taint's `timeAdded` for `draining`.
	*  When the taint carries no `timeAdded`, the operator's first observation of it. */
	since: z.string().datetime(),
	nodeType: z.string().regex(APP_POOL_NODE_TYPE_PATTERN).max(40)
}).strict();
/** One running run and the node its live attempt landed on, read off the attempt pod's
*  `spec.nodeName`. Keyed by run: one attempt of a run is live at a time (the run row carries one
*  `attempt_id`). Attempts stay inside the dispatcher (app-pools R-007), so no attempt id crosses
*  this door.
*
*  ⛔ AN ATTEMPT POD IS THE SOURCE, so a run of a `lifetime: 'standing'` pool is not here (R-12):
*  one resident pod serves many runs and carries no run label, and labelling it with one would put
*  it inside the reaper's orphan sweep and inside another attempt's network policies. That run's
*  placement rides its own `RunStatus.nodeId`. */
const appPoolRunPlacementSchema = z.object({
	runId: workRunIdSchema,
	nodeId: appPoolNodeIdSchema
}).strict();
/** The census caps derive from the platform's own ceilings (R-030, R-056). Nodes per group: the
*  old and the new node sets overlap during a scale-down, so twice `POOL_NODE_COUNT_MAX` (100,
*  `dp-sizing.ts`). Placements: one live attempt per running run of the requested pool, at most the
*  pool's concurrency ceiling `APP_POOL_CONCURRENCY_MAX` (1000, `app-pool.ts`). Groups: the data
*  plane's node-group ceiling, `APP_POOL_NODE_GROUP_MAX` (100) in `@zion/sdk-cp-dp-contracts`,
*  which the compiler enforces before any membership is written; this package is the leaf and
*  cannot import it, so the cp-dp package's test pins the equality. The composed worst case is
*  100 groups × 200 nodes, 20,000 node entries in one response. */
const APP_POOL_NODES_MAX = 200;
const APP_POOL_RUN_PLACEMENTS_MAX = APP_POOL_CONCURRENCY_MAX;
const APP_POOL_CENSUS_GROUPS_MAX = 100;
/** One node group of the pool: the group the compiler holds for it, the app's pools it serves this
*  cycle, its minimum, the count still provisioning (`max(0, requestedMin - nodes.length)`) and its
*  observed nodes. A group serves every pool of the app with one workload and machine type, so
*  `requestedMin` and `provisioning` are the group's totals and `pools` says which pools share them
*  (R-052). `groupId` is the hashed group id `describe` already exposes, never null: a pool with no
*  group answers no entry. */
const appPoolNodeGroupCensusSchema = z.object({
	groupId: z.string().regex(APP_POOL_NODE_GROUP_ID_PATTERN),
	pools: z.array(appPoolNameSchema).min(1).max(20),
	requestedMin: z.number().int().min(0),
	provisioning: z.number().int().min(0),
	nodes: z.array(appPoolNodeSchema).max(APP_POOL_NODES_MAX)
}).strict();
/** What `nodes` returns (R-028). Served from the operator's per-cycle node and attempt-pod snapshots,
*  so a call costs no Kubernetes read. `groups` is per node group. An in-mesh pool has one. An
*  out-of-mesh pool has one per org: an org caller sees its own org's group, and the app's own
*  service identity (no end-user org) sees every group of the pool. The groups are sorted by
*  `groupId` and cut at `APP_POOL_CENSUS_GROUPS_MAX`, with the count cut in `groupsOmitted`.
*  `provider: local` answers `groups: []`. `placements` carries the requested pool's running runs
*  (R-050): an org caller sees the runs of its org, the app's own service identity every run of
*  the pool. The census's trust domain is the app (R-046). The door maps the caller's org id to
*  the CP id before it filters (R-062). `observedAt` is the node snapshot's instant, so a reader
*  can tell a stale census from a quiet pool. */
const appPoolNodesSchema = z.object({
	pool: appPoolNameSchema,
	groups: z.array(appPoolNodeGroupCensusSchema).max(100),
	groupsOmitted: z.number().int().min(0),
	placements: z.array(appPoolRunPlacementSchema).max(APP_POOL_RUN_PLACEMENTS_MAX),
	observedAt: z.string().datetime()
}).strict();
/** `signals`: the harness asks for one pool's queue signals. */
const appPoolSignalsRequestSchema = z.object({ pool: appPoolNameSchema }).strict();
/** What the operator's executor writes as `isolatedJobRequestSchema.args` for one attempt, and what
*  `serveAppPool` (WS-6) parses before it calls the handler. Six keys (R-023). `pool` names the
*  pool the attempt belongs to. One fixture image can back several pools, and the pod has no other
*  source for the name: no env carries it. This frame is the pod's whole context. The pod carries
*  no credential (R-007), and the only run configuration in its env is
*  {@link APP_POOL_POD_PORT_ENV}, which carries the pipe port and nothing about the run. `orgId` is null on an
*  in-mesh pool with no attributed org; `payload` is the submitter's body, opaque to the platform.
*  `.strict()` so a frame the handler cannot read never leaves the operator: the executor parses it
*  before writing. */
const appPoolRunFrameSchema = z.object({
	pool: appPoolNameSchema,
	runId: workRunIdSchema,
	attemptId: workAttemptIdSchema,
	orgId: appPoolOrgIdSchema.nullable(),
	compute: computeVectorSchema,
	payload: z.unknown().optional()
}).strict();
/** The door's own `{ state }` frame, the first arm of {@link appPoolStreamDataSchema}.
*
*  Declared as its own schema and type so the operator's terminal watch, its stream door and this
*  contract name one shape: the watch reports a frame and the door writes it, and a second spelling
*  of the two placement keys is a second place to get their coupling wrong.
*
*  The door writes it when the stream opens and on each transition its poll observes. `nodeId` and
*  `placedAt` ride the same frame once the pod is bound, so an app learns the placement within one
*  poll interval of the bind rather than on the next census cycle (R-4). Both are absent unless the
*  stream request carried `placement: true` (R-6). */
const appPoolRunStateFrameSchema = z.object({
	state: runStateSchema,
	/** The node the run's live attempt is on, `appPoolNodeId(spec.nodeName)`, once the platform
	*  has observed one and only when the request carried `placement: true` (R-1, R-6). Both arms
	*  reach this frame (R-12): the scheduler's bind on a pool that runs one pod per attempt, the
	*  lending of an already-bound standing pod on a resident one. The same id `nodes` answers for
	*  a per-attempt run, so one id correlates the two surfaces. */
	nodeId: appPoolNodeIdSchema.optional(),
	/** The instant the platform observed that node: the bind, or the lending on a resident pool.
	*  `nodeId`'s companion: one statement writes both, so a frame carries both or neither. It is
	*  not the `running` transition's own instant, which is the claim's (R-3). */
	placedAt: z.string().datetime().optional()
}).strict();
/** What rides `data` on a `{ kind: 'progress' }` frame from `POST /internal/app-pools/runs/stream`.
*  Three arms. {@link appPoolRunStateFrameSchema} is the door's own frame: the run's state, and its
*  placement when the request asked for one. `{ type: 'progress' }` and `{ type: 'log' }` are the handler's
*  `AppPoolContext.progress(message, pct?)` and `.log(stream, chunk)`, translated by the executor and
*  forwarded live by the dispatcher through a per-process registry; nothing persists them. The two
*  handler arms carry the pipe's own field set (`isolatedJobProgressEventSchema`,
*  `isolatedJobLogEventSchema` in `isolated-pool-pipe.ts`) minus `seq`, which the frame carries. The
*  schema carries no `.max()` on `message` or `chunk`; the bound lives in the two producers, which
*  clamp a `log` chunk at `MAX_WORK_LOG_CHUNK_CHARS` before it reaches this frame (R-074): the
*  app-pool executor's translation and `south-events.ts`'s. A verbatim chunk at the dialer's 1 MiB
*  frame would let one follow pin 64 MiB in the operator's heap; the reader declares no second
*  bound, so the cap is one bound in one place. Every arm is `.strict()`, so a frame with a field nobody named fails the parse instead of riding along.
*  `workEventSchema`'s `data` stays `unknown` on the wire; this schema is what an app-pool reader
*  narrows it with. */
const appPoolStreamDataSchema = z.union([
	appPoolRunStateFrameSchema,
	z.object({
		type: z.literal("progress"),
		message: z.string(),
		pct: z.number().int().min(0).max(100).optional()
	}).strict(),
	z.object({
		type: z.literal("log"),
		stream: z.enum(["stdout", "stderr"]),
		chunk: z.string()
	}).strict()
]);
/** One frame of `POST /internal/app-pools/runs/stream` as the app-pool client yields it (R-074):
*  `workEventSchema`'s two arms with `data` narrowed to `appPoolStreamDataSchema`. `seq` is per
*  producer (`work.ts`, the `seq` docblock). `HttpAppPools.stream` parses every frame with this and
*  answers `malformed-response` on a failure, because the executor and the door build both arms
*  typed and a frame that fails here is a platform defect, never tenant input. */
const appPoolStreamEventSchema = z.discriminatedUnion("kind", [z.object({
	kind: z.literal("progress"),
	seq: z.number().int().nonnegative(),
	data: appPoolStreamDataSchema
}).strict(), z.object({
	kind: z.literal("terminal"),
	status: runStatusSchema
}).strict()]);
/** The pull reference a pool pod runs, registry prefix included, equal to the attempt pod's
*  `spec.containers[0].image`: `<host>[:port]/<repo>[/<repo>...]@sha256:<64 hex>`. The operator
*  derives it with `releasedImagePullRef` from the envelope's registry-portable `imageRef`, so the
*  digest is the CP's and the host is the cluster's. The repository segments take the same class
*  as `DIGEST_PINNED_IMAGE_REF_PATTERN`, `[A-Za-z0-9._-]`; a narrower grammar here would refuse a
*  reference the publish door admitted (R-005). A tag form is refused: the CP pins every pool
*  image by digest at publish (`DIGEST_PIN_RE`), and a tag here would mean a pod whose bytes cannot
*  be named. */
const APP_POOL_IMAGE_PULL_REF_PATTERN = /^[a-z0-9.-]+(:[0-9]{1,5})?(\/[A-Za-z0-9._-]+)+@sha256:[0-9a-f]{64}$/;
/** 768: the envelope reference's own bound, 512 (`DIGEST_PINNED_IMAGE_REF_MAX_LENGTH` in
*  `@zion/sdk-cp-dp-contracts`), plus 256 of headroom for the registry prefix the operator
*  prepends with no separator (`releasedImagePullRef`). The prefixes this repo prepends are 40
*  characters on GKE (`us-central1-docker.pkg.dev/zion-cp/zion/`) and 24 on local; a prefix over
*  256 characters with a reference at its own bound is refused here, and nothing live is. The
*  cp-dp package's test pins the 512 half. */
const APP_POOL_IMAGE_PULL_REF_MAX_LENGTH = 768;
const appPoolImagePullRefSchema = z.string().min(1).max(768).regex(APP_POOL_IMAGE_PULL_REF_PATTERN, "a digest-pinned image pull reference");
/** The bound on an app version string as it rides these two routes. 128, equal to the private
*  `APP_POOL_IMAGE_FIXTURE_VERSION_MAX_LENGTH` in `@zion/sdk-cp-dp-contracts` `desired-state.ts`,
*  the bound `AppPoolDesiredState.imageFixtureVersion` carries on the CP-to-operator wire. This
*  package is the leaf and cannot import it; the cp-dp package's test pins the two equal. The
*  publish gate admits any string starting `\d+.\d+.\d+` (`app-manifest.schema.json`), so the
*  bound is a length and never a grammar. */
const APP_VERSION_STRING_MAX_LENGTH = 128;
const appVersionStringSchema = z.string().min(1).max(128);
/** `image`: one pool of the caller's app, as `describe`. */
const appPoolImageRequestSchema = z.object({ pool: appPoolNameSchema }).strict();
/** What `image` returns (R-001, L-1). `imageRef` is the owner version's digest for the pool's
*  `image.fixture`, as the operator's catalog holds it this cycle: the image a standing member
*  runs, and the image a producer submit (a schedule or an event binding) runs. A per-attempt pod
*  submitted by a fixture pod may run another active version's digest; `runs/describe` names that
*  one. `imageFixtureVersion` is the owner version, the plain version string
*  (`AppPoolDesiredState.imageFixtureVersion`), never the slug. */
const appPoolImageReportSchema = z.object({
	pool: appPoolNameSchema,
	imageRef: appPoolImagePullRefSchema,
	imageFixtureVersion: appVersionStringSchema
}).strict();
/** `runs/describe`: one run of the caller's app, owner-tuple authorized as `runs/get` is. The same
*  declaration as `appPoolRunRefSchema`, by identity and never a copy (R-006): the door reuses
*  `readOwned`, so a malformed, unknown, reaped or foreign run id answers `404 unknown-run` through
*  one implementation, and the test pins `Object.is` on the two. */
const appPoolRunDescribeRequestSchema = appPoolRunRefSchema;
/** What `runs/describe` returns (L-6, L-11, R-004). Both fields are `.nullable()` and never
*  optional, so a `toStrictEqual` reader sees the key. `imageRef` is the pull reference the attempt
*  runs and `imageVersion` the plain version string of the version that reference belongs to: the
*  submitting pod's version when admission resolved it, the pool's owner version otherwise, both
*  written by admission or by the dispatcher at claim, in one write. `null` on both means a row
*  that never dispatched after this feature. One set and one null is refused by the `.refine`; the
*  table's check `app_pool_runs_image_version_needs_image` is the same rule one layer down. On a
*  standing pool the attempt ran on a member, whose image is the owner's, and the dispatcher
*  records the member's image here, so the row is the record of what ran. */
const appPoolRunDescribeSchema = z.object({
	runId: workRunIdSchema,
	imageRef: appPoolImagePullRefSchema.nullable(),
	imageVersion: appVersionStringSchema.nullable()
}).strict().refine((d) => d.imageVersion === null === (d.imageRef === null), {
	message: "imageRef and imageVersion are set together",
	path: ["imageVersion"]
});
//#endregion
export { APP_POOLS_PATH, APP_POOL_CENSUS_GROUPS_MAX, APP_POOL_DESCRIBE_PATH, APP_POOL_IMAGE_PATH, APP_POOL_IMAGE_PULL_REF_MAX_LENGTH, APP_POOL_IMAGE_PULL_REF_PATTERN, APP_POOL_NODES_MAX, APP_POOL_NODES_PATH, APP_POOL_NODE_ID_PATTERN, APP_POOL_NODE_STATES, APP_POOL_PLACEMENT_STATES, APP_POOL_POD_PORT, APP_POOL_POD_PORT_ENV, APP_POOL_ROUTE_PATHS, APP_POOL_RUNS_CANCEL_PATH, APP_POOL_RUNS_DESCRIBE_PATH, APP_POOL_RUNS_GET_PATH, APP_POOL_RUNS_PATH, APP_POOL_RUNS_RESULT_PATH, APP_POOL_RUNS_STREAM_PATH, APP_POOL_RUNS_SUBMIT_PATH, APP_POOL_RUN_PLACEMENTS_MAX, APP_POOL_SIGNALS_PATH, APP_VERSION_STRING_MAX_LENGTH, appPoolDescribeRequestSchema, appPoolImagePullRefSchema, appPoolImageReportSchema, appPoolImageRequestSchema, appPoolNodeGroupCensusSchema, appPoolNodeIdSchema, appPoolNodeSchema, appPoolNodesRequestSchema, appPoolNodesSchema, appPoolOrgIdSchema, appPoolRunDescribeRequestSchema, appPoolRunDescribeSchema, appPoolRunFrameSchema, appPoolRunPlacementSchema, appPoolRunRefSchema, appPoolRunStateFrameSchema, appPoolSignalsRequestSchema, appPoolStatusSchema, appPoolStreamDataSchema, appPoolStreamEventSchema, appPoolSubmitOptionsSchema, appPoolSubmitRequestSchema, appVersionStringSchema };

//# sourceMappingURL=app-pool-submit.js.map